import type { components } from '@/types/api/v1'
import { create } from 'zustand'

export type FeatureFlags = components['schemas']['FeatureFlags']
export type FeatureFlag = keyof FeatureFlags

type FlagParts<F> = F extends `${infer App}.${infer Module}.${infer Name}` ? [App, Module, Name] : never
type FlagNames = {
  [App in FlagParts<FeatureFlag>[0]]: {
    [Module in FlagParts<Extract<FeatureFlag, `${App}.${string}`>>[1]]: {
      [Name in FlagParts<Extract<FeatureFlag, `${App}.${Module}.${string}`>>[2]]: Extract<
        FeatureFlag,
        `${App}.${Module}.${Name}`
      >
    }
  }
}

function flagNamePart(parts: string[]): unknown {
  if (parts.length === 3) return parts.join('.')
  return new Proxy({}, { get: (_, key) => (typeof key === 'string' ? flagNamePart([...parts, key]) : undefined) })
}

export const Flags = flagNamePart([]) as FlagNames

type StoredFlags = Partial<Record<string, boolean>>
type FlagLayers = { customer: StoredFlags | null; user: StoredFlags | null; defaults: StoredFlags | null }

const CUSTOMER_KEY = 'featureFlags:customer'
const DEFAULTS_KEY = 'featureFlags:defaults'

function isFlags(value: unknown): value is StoredFlags {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readFlags(key: string | null): StoredFlags | null {
  if (!key) return null
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? 'null')
    return isFlags(parsed) ? parsed : null
  } catch {
    return null
  }
}

function writeFlags(key: string | null, flags: StoredFlags) {
  if (!key) return
  try {
    localStorage.setItem(key, JSON.stringify(flags))
  } catch {
    // storage full or blocked: the in-memory value still applies
  }
}

function currentUserKey(): string | null {
  try {
    const user = localStorage.getItem('user')
    return localStorage.getItem('refresh') && user ? `featureFlags:user:${user}` : null
  } catch {
    return null
  }
}

const useFlagLayers = create<FlagLayers>(() => ({
  customer: readFlags(CUSTOMER_KEY),
  user: readFlags(currentUserKey()),
  defaults: readFlags(DEFAULTS_KEY),
}))

function enabledIn(layers: FlagLayers, flag: FeatureFlag): boolean {
  return (layers.user?.[flag] ?? layers.customer?.[flag] ?? layers.defaults?.[flag]) === true
}

export function setFlags(flags: FeatureFlags | undefined) {
  if (!isFlags(flags)) return
  writeFlags(currentUserKey(), flags)
  useFlagLayers.setState({ user: flags })
}

export function setCustomerFlags(flags: FeatureFlags | undefined) {
  if (!isFlags(flags)) return
  writeFlags(CUSTOMER_KEY, flags)
  useFlagLayers.setState({ customer: flags })
}

export function setFlagDefaults(flags: FeatureFlags | undefined) {
  if (!isFlags(flags)) return
  writeFlags(DEFAULTS_KEY, flags)
  useFlagLayers.setState({ defaults: flags })
}

export function loadStoredUserFlags() {
  useFlagLayers.setState({ user: readFlags(currentUserKey()) })
}

export function isFeatureEnabled(flag: FeatureFlag): boolean {
  return enabledIn(useFlagLayers.getState(), flag)
}

export function useFeatureEnabled(flag: FeatureFlag): boolean {
  return useFlagLayers((layers) => enabledIn(layers, flag))
}
